//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Clock, Check, X, Ban } from 'lucide-react';
import { Spinner } from '../../ui/spinner';
import { cn } from '../../../lib/utils';
import ToolCallDetails from './ToolCallDetails';
import {
  isApprovedForDisplay,
  pickForToolStatus,
  type ToolTraceRow,
} from './turnGrouping';
import { TOOL_CARD_BORDER_CLASS, TOOL_CARD_BG_CLASS } from './toolCardStyle';
import { useToggleSet } from './useToggleSet';

export interface TurnTracePanelProps {
  turnId: string;
  rows: ToolTraceRow[];
  isOpen: boolean;
}

// ms -> a short human string. Not specified further than "duration when
// known" in the design; this repo has no existing duration formatter to
// reuse, so this is a plain, judgment-call implementation.
function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms))}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const STATUS_KEYS = {
  running: ['ai-turn-trace-status-running', 'Running'],
  pending_approval: [
    'ai-turn-trace-status-pending-approval',
    'Awaiting approval',
  ],
  success: ['ai-turn-trace-status-success', 'Succeeded'],
  error: ['ai-turn-trace-status-error', 'Failed'],
} as const;

// Icons, not words — ToolCallBox (the inline card two turns above) already
// uses a spinner/amber-chip/check/x vocabulary for these same four states;
// a second, all-caps text vocabulary here (SUCCEEDED/FAILED) read as a
// second visual language for the same information, and treated a routine
// ReAct retry-after-failure the same as an incident. A rejected call is
// its own icon (Ban, neutral) rather than the same red X as a genuine
// execution error — it never ran at all, which is a different fact than
// "ran and failed" (found live 2026-09-05).
const StatusIcon: React.FC<{ row: ToolTraceRow }> = ({ row }) => {
  // Rejected is a gateDecision concern, not a ToolStatus value — handled
  // here, before pickForToolStatus, rather than folded into its cases.
  if (row.gateDecision === 'rejected') {
    return (
      <Ban className="size-3 shrink-0 text-slate-400" aria-hidden="true" />
    );
  }
  return pickForToolStatus(row.status, {
    running: <Spinner size="sm" className="size-3 shrink-0 text-violet-600" />,
    pending_approval: (
      <Clock className="size-3 shrink-0 text-amber-600" aria-hidden="true" />
    ),
    success: (
      <Check className="size-3 shrink-0 text-green-600" aria-hidden="true" />
    ),
    error: <X className="size-3 shrink-0 text-red-600" aria-hidden="true" />,
  });
};

interface RowProps {
  row: ToolTraceRow;
  isExpanded: boolean;
  onToggle: () => void;
}

const TraceRow: React.FC<RowProps> = ({ row, isExpanded, onToggle }) => {
  const { t } = useTranslation();
  const [statusKey, statusDefault] = STATUS_KEYS[row.status];
  // The icon is aria-hidden and decorative; this text is the real
  // accessible name for the status, kept visually hidden so sighted users
  // read the icon instead — status stays conveyed by text, not color alone,
  // it just isn't the loudest thing on the row.
  const statusLabel =
    row.gateDecision === 'rejected'
      ? t('ai-turn-trace-gate-rejected', 'Rejected — never ran')
      : t(statusKey, statusDefault);
  // Prefer a reported duration when one is ever available (FR-012); no
  // backend emits one today (research.md R1), so this is currently always
  // the client-observed durationMs. No zero, no placeholder (FR-013).
  const durationMs = row.reportedDurationMs ?? row.durationMs;

  return (
    <div
      id={`ai-module-turn-trace-row-${row.toolCallId}`}
      data-testid={`ai-module-turn-trace-row-${row.toolCallId}`}
      className="border-b border-gray-100 last:border-b-0"
    >
      <button
        type="button"
        className={cn(
          'flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs',
          'hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        )}
        aria-expanded={isExpanded}
        aria-controls={`ai-module-turn-trace-row-details-${row.toolCallId}`}
        aria-label={t(
          'ai-turn-trace-expand-row',
          `Show details for ${row.toolName}`,
          {
            tool: row.toolName,
          },
        )}
        onClick={onToggle}
      >
        <StatusIcon row={row} />
        <span className="flex-1 truncate font-semibold text-violet-800">
          {row.toolName}
        </span>
        <span className="sr-only">{statusLabel}</span>
        {isApprovedForDisplay(row) && (
          <span
            data-testid={`ai-module-turn-trace-row-gate-${row.toolCallId}`}
            className="shrink-0 text-xs font-medium text-slate-400"
          >
            {t('ai-turn-trace-gate-approved', 'Approved')}
          </span>
        )}
        {/* Fixed-width slot, always rendered — reserving the space rather
            than conditionally mounting it. A duration arrives later, on its
            own, well after the row is already visible with its "Approved"
            label sitting there; letting this span appear/disappear made
            flex-1's box (and everything before this slot) reflow the
            instant a result came back, visibly shifting the label the user
            was already looking at. No text is invented while empty — only
            the box is reserved (FR-013 is about the value, not the layout). */}
        <span className="w-12 shrink-0 text-right text-xs text-slate-400">
          {durationMs !== undefined ? formatDuration(durationMs) : ''}
        </span>
        {isExpanded ? (
          <ChevronUp
            className="size-3.5 shrink-0 text-slate-400"
            aria-hidden="true"
          />
        ) : (
          <ChevronDown
            className="size-3.5 shrink-0 text-slate-400"
            aria-hidden="true"
          />
        )}
      </button>
      {isExpanded && (
        <div
          id={`ai-module-turn-trace-row-details-${row.toolCallId}`}
          className="px-2 pb-2"
        >
          <ToolCallDetails
            toolArgs={row.toolArgs}
            toolResult={row.toolResult}
            resultBlocks={row.toolResultBlocks}
            toolStatus={row.status}
            outputRef={row.toolOutputRef}
            toolCallId={row.toolCallId}
          />
        </div>
      )}
    </div>
  );
};

// The record: one row per dispatched call, in the order `rows` is given
// (dispatch order, per turnGrouping.groupMessagesByTurn). Never renders
// approval affordances — a gated call's approve/reject controls live only
// on its inline card in the conversation column (FR-014); this panel is a
// read-only history of what ran.
const TurnTracePanel: React.FC<TurnTracePanelProps> = ({
  turnId,
  rows,
  isOpen,
}) => {
  const [expandedIds, toggleRow] = useToggleSet();

  if (!isOpen) return null;

  return (
    <div
      id={`ai-module-turn-trace-panel-${turnId}`}
      data-testid={`ai-module-turn-trace-panel-${turnId}`}
      className={cn(
        'mt-1 overflow-y-auto rounded-lg border',
        TOOL_CARD_BORDER_CLASS,
        TOOL_CARD_BG_CLASS,
        // Bounded height so a long record scrolls inside itself rather than
        // nesting unbounded scrolling inside the conversation column
        // (research.md R6) — mirrors ToolCallBox's own output scroller.
        'max-h-[320px]',
      )}
    >
      {rows.map((row) => (
        <TraceRow
          key={row.toolCallId}
          row={row}
          isExpanded={expandedIds.has(row.toolCallId)}
          onToggle={() => toggleRow(row.toolCallId)}
        />
      ))}
    </div>
  );
};

export default TurnTracePanel;
